import { AsyncPipe, CommonModule } from '@angular/common';
import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatCard, MatCardContent, MatCardHeader, MatCardTitle } from '@angular/material/card';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  distinctUntilChanged,
  EMPTY,
  map,
  merge,
  of,
  shareReplay,
  startWith,
  switchMap,
} from 'rxjs';
import { TSP } from '../../core/models';
import { AdminService } from '../../core/services/admin/admin.service';
import { PortalService } from '../../core/services/portal/portal.service';

@Component({
  selector: 'app-file-management',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButton,
    MatCard,
    MatCardHeader,
    MatCardTitle,
    MatCardContent,
    MatCheckbox,
    MatFormField,
    MatLabel,
    MatInput,
    MatRadioGroup,
    MatRadioButton,
    MatSelect,
    MatOption,
    AsyncPipe,
  ],
  templateUrl: './file-management.component.html',
  styleUrls: ['./file-management.component.scss'],
})
export class FileManagementComponent {
  private readonly adminService = inject(AdminService);
  private readonly portalService = inject(PortalService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly refresh$ = new BehaviorSubject<number>(new Date().getTime());
  private readonly destroyRef = inject(DestroyRef);

  public readonly portalControl = new FormControl<number | null>(null);
  public readonly limit = new FormControl<number>(100, { nonNullable: true });
  public readonly forceProcessing = new FormControl<boolean>(false, { nonNullable: true });
  public readonly batchId = new FormControl<string>('', { nonNullable: true });
  public readonly provider = new FormControl<'openai' | 'anthropic'>('openai', { nonNullable: true });

  public readonly portals$ = this.portalService.getAllPortals().pipe(
    map((r) => r.items),
    catchError(() => {
      this.snackBar.open('Не вдалося завантажити портали.', 'OK', { duration: 4000 });
      return of([]);
    }),
  );

  private readonly portalId$ = this.portalControl.valueChanges.pipe(distinctUntilChanged(), startWith(null));

  public readonly statistic$ = combineLatest([this.portalId$, this.refresh$]).pipe(
    switchMap(([portalId]) =>
      portalId
        ? this.adminService
            .getPortalFilesStatistic(
              portalId,
              [TSP.Internal.File.File.Type.CV],
              [
                TSP.Internal.File.File.ProcessingStatus.NEW,
                TSP.Internal.File.File.ProcessingStatus.NORMALIZED,
                TSP.Internal.File.File.ProcessingStatus.PARSED,
              ],
            )
            .pipe(
              catchError(() => {
                this.snackBar.open('Не вдалося завантажити статистику по порталу.', 'OK', { duration: 4000 });

                return EMPTY;
              }),
            )
        : of(null),
    ),
    shareReplay(1),
  );

  public readonly statisticLoading$ = merge(
    this.portalId$.pipe(map(() => true)),
    this.statistic$.pipe(map(() => false)),
  );
  public readonly classifyCount$ = this.statistic$.pipe(
    map((s) =>
      s
        ? (s.statuses[TSP.Internal.File.File.ProcessingStatus.NEW] ?? 0) +
          (s.statuses[TSP.Internal.File.File.ProcessingStatus.NORMALIZED] ?? 0)
        : null,
    ),
  );
  public readonly vectorizeCount$ = this.statistic$.pipe(
    map((s) => (s ? (s.statuses[TSP.Internal.File.File.ProcessingStatus.PARSED] ?? 0) : null)),
  );

  public refreshStatistics(): void {
    const portalId = this.portalControl.value;
    if (portalId) {
      this.refresh$.next(new Date().getTime());
    }
  }

  public classifyFiles(): void {
    const portalId = this.portalControl.value;
    if (!portalId) {
      this.snackBar.open('Оберіть портал.', 'OK', { duration: 3000 });
      return;
    }

    this.adminService
      .classifyFiles(portalId, this.limit.value, this.forceProcessing.value)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.snackBar.open('Класифікацію файлів запущено.', 'OK', { duration: 3000 }),
        error: () => this.snackBar.open('Не вдалося запустити класифікацію файлів.', 'OK', { duration: 4000 }),
      });
  }

  public vectorizeTextContent(): void {
    const portalId = this.portalControl.value;
    if (!portalId) {
      this.snackBar.open('Оберіть портал.', 'OK', { duration: 3000 });
      return;
    }

    this.adminService
      .vectorizeTextContent(portalId, this.limit.value)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.snackBar.open('Векторизацію текстового контенту запущено.', 'OK', { duration: 3000 }),
        error: () => this.snackBar.open('Не вдалося запустити векторизацію.', 'OK', { duration: 4000 }),
      });
  }

  public processOrphanTextContent(strType: string): void {
    const portalId = this.portalControl.value;
    if (!portalId) {
      this.snackBar.open('Оберіть портал.', 'OK', { duration: 3000 });
      return;
    }

    let type: TSP.Internal.File.AI.OrphanType;

    switch (strType) {
      case 'missed_embeddings':
        type = TSP.Internal.File.AI.OrphanType.MISSED_EMBEDDINGS;
        break;
      case 'missed_sections':
      default:
        type = TSP.Internal.File.AI.OrphanType.MISSED_SECTIONS;
    }

    this.adminService
      .processOrphanTextContent(type, portalId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.snackBar.open('Обробку orphan контенту запущено.', 'OK', { duration: 3000 }),
        error: () => this.snackBar.open('Не вдалося запустити обробку orphan контенту.', 'OK', { duration: 4000 }),
      });
  }

  public processExistedBatch(): void {
    const batchId = this.batchId.value;
    const provider = this.provider.value;

    if (!batchId) {
      this.snackBar.open('Введіть Batch ID.', 'OK', { duration: 3000 });
      return;
    }

    this.adminService
      .processExistedBatch(batchId, provider)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.snackBar.open('Обробку існуючого батча запущено.', 'OK', { duration: 3000 }),
        error: () => this.snackBar.open('Не вдалося запустити обробку батча.', 'OK', { duration: 4000 }),
      });
  }
}
